import { useGetUnitTrafficSignalStrength } from '@apps-common/apis/portal/unit'
import { ModalButton, SignalStrengthModal } from '@apps-common/components/modals'
import { commonIcons } from '@apps-common/config/icons'
import { CARD_RADIUS_STYLES } from '@apps-common/config/styles'
import { useSignalStrengthIcon } from '@apps-common/hooks/useSignalStrengthIcon'
import { useWindowDimensions } from '@apps-common/hooks/useWindowDimensions'
import { modalSlideUpEnterAnimation, modalSlideUpLeaveAnimation } from '@apps-common/lib/modal-animations'
import { IUnitGenericDetailsModel } from '@apps-common/types/portal/unit'
import { calcSignalStrength } from '@apps-common/utils/helpers'
import classNames from 'classnames'
import { useMemo } from 'react'
import { SignalStrengthKpiCardSkeleton } from './SignalStrengthKpiCardSkeleton'

interface IProps {
  unit?: IUnitGenericDetailsModel
}

export const SignalStrengthKpiCard: React.FC<IProps> = ({ unit }) => {
  const { ipAddress, imei } = unit || {}
  const { isMobile, isMobileLg } = useWindowDimensions()
  const { isLoading, isError, data, refetch } = useGetUnitTrafficSignalStrength({
    config: {
      enabled: !!unit && (isMobile || isMobileLg)
    },
    identifiers: `${ipAddress},${imei}`,
    skip: 0,
    take: 1
  })

  const signalStrength = useMemo(() => data?.[0]?.signalStrength || '', [data])

  const signal = useSignalStrengthIcon(parseInt(signalStrength, 10))
  const calculatedSignalStrength = calcSignalStrength(parseInt(signalStrength, 10))

  const loading = !unit || isLoading || isError

  return (
    <>
      {loading && <SignalStrengthKpiCardSkeleton />}
      {!loading && (
        <ModalButton
          component={SignalStrengthModal}
          disabled={loading}
          componentProps={{
            ipAddress,
            imei
          }}
          classes=''
          enterAnimation={modalSlideUpEnterAnimation}
          leaveAnimation={modalSlideUpLeaveAnimation}
        >
          <div
            className={classNames(
              'relative w-full flex flex-col bg-surface border border-border px-4 py-4',
              CARD_RADIUS_STYLES,
              loading && 'opacity-50 hover:cursor-default'
            )}
          >
            <div className='w-full h-full flex flex-col items-center justify-center'>
              <div className='w-full flex'>
                <div className='relative rounded-3xl mx-auto bg-surface-2 p-4 min-w-[80%]'>
                  <div className='absolute right-[-5px] top-[-5px] z-[2] p-[5px] rounded-full bg-brand text-on-brand'>
                    <commonIcons.cellTowerSignal className='text-[0.9rem]' />
                  </div>
                  <div className='flex flex-col items-center justify-center gap-2 text-text'>
                    <div
                      className={classNames(
                        signal.bg,
                        signal.color,
                        'text-[1.1rem] flex items-center justify-between gap-2 px-3 rounded-full'
                      )}
                    >
                      <div>{calculatedSignalStrength}</div>
                      {signal.Icon}
                    </div>
                    <div className='text-[0.75rem]'>Latest Signal</div>
                  </div>
                </div>
              </div>
              <div className='w-full flex items-center justify-between text-[0.85em] jost text-text px-2 mt-2'>
                <p>All Signals</p>
                <div className='flex items-center justify-center p-1 rounded-full bg-surface-2'>
                  <commonIcons.arrowAngled />
                </div>
              </div>
            </div>
          </div>
        </ModalButton>
      )}
    </>
  )
}
